import React, { useEffect, useState } from 'react';
import { Permissions, useAuth } from '@kp/react-sdk';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CapabilityDetails } from './CapabilityDetails';
import { useCapabilityDetailsQuery } from '../../../__generated__/types';
import { useBreadcrumb } from '../../../contexts/breadcrumb-context';
import { makeHierarchyBreadcrumb } from '../../../utils/breadcrumb';
import * as Entities from '../../../constants/Entities';
import { ErrorMessage } from '../../../components/Errors';
import { entityLocation } from '../../../utils/entity';
import { CapabilityDeleteContainer } from '../delete/CapabilityDeleteContainer';
import { useHeader } from '../../../contexts/header-context';
import { NotFound } from '../../errorScreens';

export const CapabilityDetailsContainer: React.FC = () => {
  const { t } = useTranslation(['capabilities', 'general']);
  const navigate = useNavigate();
  const params = useParams();
  const { setTitle } = useHeader();
  const { hasPermission } = useAuth();
  const { capabilityId } = params;

  const [deleteMode, setDeleteMode] = useState(false);

  const { data, loading, error } = useCapabilityDetailsQuery({
    variables: { capabilityId },
  });

  const capability = data?.capability;
  const hierarchy = capability
    ? makeHierarchyBreadcrumb(
        [
          {
            type: Entities.CAPABILITY,
            id: capability.id,
            name: capability.name || capability.id,
          },
        ],
        t,
      )
    : [];

  useBreadcrumb(hierarchy);

  const handleDeleteCancel = () => setDeleteMode(false);
  const handleDeleteError = () => setDeleteMode(false);
  const handleDelete = () => setDeleteMode(true);
  const handleDeleteComplete = () =>
    navigate(entityLocation(Entities.CAPABILITY, ''));
  const handleEdit = () =>
    navigate(`${entityLocation(Entities.CAPABILITY, capabilityId)}/edit`);

  useEffect(() => {
    setTitle({ main: capability?.name });
  }, [setTitle, capability]);

  if (error) {
    return <ErrorMessage error={error} />;
  }
  if (!loading && !capability) {
    return (
      <NotFound
        title={t('capabilities:details.notFound.title') ?? ''}
        subtitle={t('capabilities:details.notFound.subtitle') ?? ''}
        buttonText={t('capabilities:details.notFound.buttonText') ?? ''}
        buttonOnClick={() => navigate(Entities.CAPABILITY.path)}
      />
    );
  }

  const isLocked =
    !!capability?.isLocked &&
    !hasPermission(Permissions.CapabilitiesWriteLocked);
  const deletePermission = capability?.isLocked
    ? hasPermission(Permissions.CapabilitiesWriteLocked)
    : hasPermission(Permissions.CapabilitiesWrite);
  const editPermission =
    hasPermission(Permissions.CapabilitiesWrite) ||
    hasPermission(Permissions.CapabilitiesWriteLocked);

  return (
    <>
      <CapabilityDetails
        loading={loading}
        name={capability?.name ?? ''}
        description={capability?.description ?? ''}
        dataType={capability?.dataType ?? null}
        isGlobal={capability?.isGlobal ?? false}
        isLocked={isLocked}
        onDelete={deletePermission ? handleDelete : undefined}
        onEdit={editPermission ? handleEdit : undefined}
      />
      <CapabilityDeleteContainer
        id={capabilityId}
        isOpen={deleteMode}
        onCompleted={handleDeleteComplete}
        onCancel={handleDeleteCancel}
        onError={handleDeleteError}
      />
    </>
  );
};
